import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { APP_CONFIG } from '@/app/core/config/app-config.token';
import { EntitlementService } from '@/app/core/entitlements/entitlement.service';
import { NAV_SECTIONS } from '@/app/shared/nav/nav-sections.token';
import { NavItem, NavSection } from '@/app/shared/nav/nav-section.model';

@Injectable({ providedIn: 'root' })
export class ShellStore {
  private readonly config = inject(APP_CONFIG);
  private readonly router = inject(Router);
  private readonly doc = inject(DOCUMENT);
  private readonly entitlements = inject(EntitlementService);

  private readonly rawSections =
    (inject(NAV_SECTIONS, { optional: true }) as NavSection[] | null) ?? [];

  readonly drilledItem = signal<NavItem | null>(null);
  readonly animDir = signal<'in' | 'out' | 'none'>('none');
  private readonly expandedLabels = signal<ReadonlySet<string>>(new Set());

  readonly activeUrl = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects),
    ),
    { initialValue: this.doc.location.pathname },
  );

  readonly sections = computed<NavSection[]>(() => this._filterSections(this.rawSections));
  readonly envLabel = this.config.environment;

  constructor() {
    effect(() => this._syncFromUrl(this.activeUrl() ?? ''));
  }

  drillIn(item: NavItem): void {
    if (!item.children?.length) {
      if (item.route) this.router.navigate([item.route]);
      return;
    }
    this.animDir.set('in');
    this.drilledItem.set(item);
    const leaf = this._firstLeaf(item.children);
    if (leaf?.route) this.router.navigate([leaf.route]);
  }

  drillOut(): void {
    this.animDir.set('out');
    this.drilledItem.set(null);
  }

  isExpanded(item: NavItem): boolean {
    return this.expandedLabels().has(item.label);
  }

  toggleExpanded(item: NavItem): void {
    this.expandedLabels.update((labels) => {
      const next = new Set(labels);
      if (next.has(item.label)) next.delete(item.label);
      else next.add(item.label);
      return next;
    });
  }

  isItemActive(item: NavItem): boolean {
    const url = this.activeUrl() ?? '';
    if (item.route) {
      return item.route === '/'
        ? url === '/'
        : url === item.route || url.startsWith(item.route + '/');
    }
    return item.children ? this._urlMatchesAnyDescendant(item.children, url) : false;
  }

  private _syncFromUrl(url: string): void {
    let drilled: NavItem | null = null;
    for (const section of this.rawSections) {
      for (const item of section.items) {
        if (!item.children?.length || !this._urlMatchesAnyDescendant(item.children, url)) continue;
        if (item.expandable) this._expand(item);
        else drilled ??= item;
      }
    }
    this.drilledItem.set(drilled);
  }

  private _expand(item: NavItem): void {
    // update() no registra dependencia: colapsar manualmente no re-dispara este effect
    this.expandedLabels.update((labels) =>
      labels.has(item.label) ? labels : new Set(labels).add(item.label),
    );
  }

  private _urlMatchesAnyDescendant(items: NavItem[], url: string): boolean {
    return items.some((item) => {
      if (item.route) {
        return item.route === '/'
          ? url === '/'
          : url === item.route || url.startsWith(item.route + '/');
      }
      return item.children ? this._urlMatchesAnyDescendant(item.children, url) : false;
    });
  }

  private _firstLeaf(items: NavItem[]): NavItem | null {
    for (const item of items) {
      if (item.route) return item;
      if (item.children) {
        const found = this._firstLeaf(item.children);
        if (found) return found;
      }
    }
    return null;
  }

  private _filterSections(sections: NavSection[]): NavSection[] {
    return sections
      .map((section) => ({ ...section, items: this._filterItems(section.items) }))
      .filter((section) => section.items.length > 0);
  }

  private _filterItems(items: NavItem[]): NavItem[] {
    return items.reduce<NavItem[]>((visible, item) => {
      const filtered = this._filterItem(item);
      if (filtered) visible.push(filtered);
      return visible;
    }, []);
  }

  private _filterItem(item: NavItem): NavItem | null {
    if (item.children?.length) {
      const children = this._filterItems(item.children);
      return children.length > 0 ? { ...item, children } : null;
    }
    if (!item.permission) return item;
    return this.entitlements.hasPermission(item.permission) ? item : null;
  }
}
